import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import { ApplicationDefinitionService } from 'src/app/shared/services/application-definition.service';
import { ApplicationDefinitionSummary } from 'src/app/shared/models/application-definition/application-definition-summary';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PreviewApplicationModalComponent } from 'src/app/shared/components/preview-application-modal/preview-application-modal.component';
import { FormService } from 'src/app/shared/services/form.service';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { MembersService } from 'src/app/shared/services/members.service';
import { finalize } from 'rxjs/operators';
import { MatStepper } from '@angular/material/stepper';
import { Person } from 'src/app/people/models/person';
import { NumberOfRecipientsValidator } from './number-of-recipients.validator';
import { PersonDetailsService } from 'src/app/shared/services/person-details.service';
import { ApplicationService } from '../../services/application.service';
import { PeopleFilterService } from 'src/app/people/people-filter.service';
import { ODataFilterBuilder } from '../../odata-filter-models/odata-filter-builder';
import { PeopleFilterFields } from 'src/app/people/models/peopleFilterFields';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { FilterOperatorType } from '../odata-filter/models/filter-operator-type';
import * as _ from 'lodash';

@Component({
  selector: 'send-application',
  templateUrl: './send-application.component.html',
  styleUrls: ['./send-application.component.scss'],
})
export class SendApplicationComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  private formSubscription: Subscription;
  private readonly defaultStepTwoFormValues = {
    sendAll: true,
    sendReminder: true,
    sendNew: true,
  };
  @ViewChild(MatStepper)
  private stepper: MatStepper;

  public readonly maxRecipients = 1000;
  public newRecipientsCount: number;
  public resendRecipientsCount: number;
  public stepOneForm: UntypedFormGroup;
  public stepTwoForm: UntypedFormGroup;
  public applicationDefinitions: ApplicationDefinitionSummary[];
  public isStepTwoLoading: boolean;
  public isSendingEmails: boolean;
  public person: Person;
  public selectedPeopleIds: string[];

  constructor(
    private applicationDefinitionService: ApplicationDefinitionService,
    private applicationService: ApplicationService,
    private personDetailsService: PersonDetailsService,
    private membersService: MembersService,
    private formService: FormService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private peopleFilterService: PeopleFilterService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.applicationDefinitionService.applicationDefinitionsAvailableToSend.subscribe(
        (definitions) => {
          this.applicationDefinitions = definitions;
        }
      ),
      this.personDetailsService.personFetched.subscribe((person) => {
        this.person = person;
      }),
      this.peopleFilterService.selectedPeopleIds.subscribe((ids) => {
        this.selectedPeopleIds = ids;
      })
    );

    this.initializeStepOneForm();

    // If the route has a personId, then we're in single person mode.
    // Otherwise, we're in multiple person mode.
    const personId = this.route.parent.snapshot.params['personId'];
    if (personId) {
      if (
        this.personDetailsService.currentPerson &&
        this.personDetailsService.currentPerson.id == personId
      ) {
        this.person = this.personDetailsService.currentPerson;
      } else {
        this.personDetailsService.getPerson(personId);
      }
    } else {
      this.initializeStepTwoForm();
    }

    this.applicationDefinitionService.getApplicationsAvailableToSend();
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.peopleFilterService.selectedPeopleIds.subscribe(() => {
        this.processSearchResults();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  close(): void {
    this.router.navigate(['..'], { relativeTo: this.route });
  }

  onApplicationDefinitionSelectionChanged() {
    if (this.isSendingToSearchResult) {
      this.resetStepTwoForm();
    }
  }

  stepChanged($event: StepperSelectionEvent) {
    if ($event.selectedIndex !== 1) return;

    if (this.isSendingToSearchResult) {
      this.dryRun();
    }
  }

  previewApplication(): void {
    if (!this.formSubscription) {
      this.formSubscription = this.formService.form.subscribe((applicationDefinitionForm) => {
        if (!applicationDefinitionForm) return;
        PreviewApplicationModalComponent.open(this.dialog, applicationDefinitionForm);
      });
      this.subscriptions.push(this.formSubscription);
    }

    this.formService.getById(this.selectedApplicationDefinition.id);
  }

  onSendClicked(): void {
    if (this.isSendingEmails) return;

    this.isSendingEmails = true;
    if (this.isSendingToSearchResult) {
      this.sendBulkApplications();
    } else {
      this.sendSingleApplication();
    }
  }

  sendAllChanged($event: MatCheckboxChange): void {
    if ($event.checked) {
      this.sendNew = true;
      this.sendReminder = true;
    }
  }

  sendReminderOrNewChanged($event: MatCheckboxChange): void {
    if (!$event.checked) {
      this.sendAll = false;
    }
  }

  get isStepOneValid(): boolean {
    return this.stepOneForm?.valid;
  }

  get isSendingToSearchResult(): boolean {
    return !!this.stepTwoForm;
  }

  get isStepTwoValid(): boolean {
    return this.stepTwoForm?.valid;
  }

  get isStepThreeValid(): boolean {
    return (
      this.isStepOneValid &&
      (!this.isSendingToSearchResult || this.isStepTwoValid) &&
      !this.isSendingEmails
    );
  }

  get hasTooManyRecipientsError(): boolean {
    return this.stepTwoForm.errors?.tooManyRecipients;
  }

  get hasNoRecipientsError(): boolean {
    return this.stepTwoForm.errors?.noRecipients;
  }

  get potentialRecipientsCount(): number {
    return this.peopleFilterService.selectedPeopleCount;
  }

  get recipientsCount(): number {
    if (this.sendAll) return this.potentialRecipientsCount;

    return (
      (this.sendNew ? this.newRecipientsCount : 0) +
      (this.sendReminder ? this.resendRecipientsCount : 0)
    );
  }

  get showApplicationDefinitionRequiredError(): boolean {
    var formControl = this.applicationDefinitionFormControl;
    return formControl.hasError('required') && formControl.touched;
  }

  get selectedApplicationDefinition(): ApplicationDefinitionSummary {
    return this.applicationDefinitionFormControl.value as ApplicationDefinitionSummary;
  }

  get applicationDefinitionFormControl(): UntypedFormControl {
    return this.stepOneForm.get('applicationDefinition') as UntypedFormControl;
  }

  get sendAll(): boolean {
    return this.sendAllFormControl?.value as boolean;
  }

  set sendAll(value: boolean) {
    this.sendAllFormControl.setValue(value);
  }

  get sendAllFormControl(): UntypedFormControl {
    return this.stepTwoForm?.get('sendAll') as UntypedFormControl;
  }

  get sendNew(): boolean {
    return this.sendNewFormControl?.value as boolean;
  }

  set sendNew(value: boolean) {
    this.sendNewFormControl.setValue(value);
  }

  get sendNewFormControl(): UntypedFormControl {
    return this.stepTwoForm?.get('sendNew') as UntypedFormControl;
  }

  get sendReminder(): boolean {
    return this.sendReminderFormControl?.value as boolean;
  }

  set sendReminder(value: boolean) {
    this.sendReminderFormControl.setValue(value);
  }

  get sendReminderFormControl(): UntypedFormControl {
    return this.stepTwoForm?.get('sendReminder') as UntypedFormControl;
  }

  private initializeStepOneForm(): void {
    this.stepOneForm = this.formBuilder.group({
      applicationDefinition: [null, [Validators.required]],
    });
  }

  private initializeStepTwoForm(): void {
    this.stepTwoForm = this.formBuilder.group(this.defaultStepTwoFormValues, {
      validators: [NumberOfRecipientsValidator(this.maxRecipients, () => this.recipientsCount)],
    });
  }

  private resetStepTwoForm(): void {
    this.stepTwoForm.reset(this.defaultStepTwoFormValues);
  }

  private processSearchResults(): void {
    switch (this.stepper.selectedIndex) {
      case 0: // Do nothing.
        break;
      default:
        this.stepper.selectedIndex = 1;
        this.dryRun();
        this.resetStepTwoForm();
        break;
    }
  }

  private dryRun() {
    this.isStepTwoLoading = true;
    this.peopleFilterService
      .getAllPeopleIds(
        ODataFilterBuilder.buildSimpleFilter(
          PeopleFilterFields.status,
          PersonStatus.Archived,
          FilterOperatorType.ne
        )
      )
      .subscribe((peopleIds) => {
        let ids = peopleIds.map((x) => x.id);
        let selectedIds = _.intersection(this.selectedPeopleIds, ids);
        this.applicationService
          .bulkApplicationsDryRun(selectedIds, this.selectedApplicationDefinition.id)
          .pipe(finalize(() => (this.isStepTwoLoading = false)))
          .subscribe((data) => {
            this.newRecipientsCount = data.new;
            this.resendRecipientsCount = data.resend;
          });
      });
  }

  private sendBulkApplications(): void {
    this.peopleFilterService
      .getAllPeopleIds(
        ODataFilterBuilder.buildSimpleFilter(
          PeopleFilterFields.status,
          PersonStatus.Archived,
          FilterOperatorType.ne
        )
      )
      .subscribe((peopleIds) => {
        let ids = peopleIds.map((x) => x.id);
        let selectedIds = _.intersection(this.selectedPeopleIds, ids);
        this.applicationService
          .sendBulkApplications(
            selectedIds,
            this.selectedApplicationDefinition.id,
            this.sendAll,
            this.sendNew,
            this.sendReminder
          )
          .pipe(finalize(() => (this.isSendingEmails = false)))
          .subscribe((res) => {
            this.membersService.openSnackBar(
              'An email with a link to your form has been sent to each person.',
              true
            );
            this.close();
          });
      });
  }

  private sendSingleApplication(): void {
    this.personDetailsService
      .sendSingleApplication(this.person.id, this.selectedApplicationDefinition.id)
      .pipe(finalize(() => (this.isSendingEmails = false)))
      .subscribe((res) => {
        this.membersService.openSnackBar(
          `An email with a link to your form has been sent to ${this.person.fullName} (${this.person.email}).`,
          true
        );
        this.close();
      });
  }
}
